import { Injectable } from '@angular/core';
import { AbstractReadOnlyDataProvider } from '@epicuro-next/data-providers/base-data-provider';
import { HttpBodyProviderService } from '@epicuro-next/data-providers/http-provider';
import { ProductBasketProviderService } from '@epicuro-next/data-providers/product-provider';
import { ProductListResponseDto } from '@epicuro-next/data/engagement/engagement-models';
import {
  ApplicationState,
  selectActiveAccountBrandOrganizations,
} from '@epicuro-next/state/accounts-state';
import { UnwrapObservable } from '@epicuro-next/utilities';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';
import { SearchQuery } from 'graphql-api/gql-api-interfaces';
import { map, switchMap } from 'rxjs';

@Injectable()
export class ListWithProductSearchProviderService
  implements
    AbstractReadOnlyDataProvider<{
      listData: ProductListResponseDto;
      products: UnwrapObservable<
        ReturnType<ProductBasketProviderService['read']>
      >['products'];
    }>
{
  constructor(
    private readonly httpBodyProviderService: HttpBodyProviderService,
    private readonly productBasketProviderService: ProductBasketProviderService,
    private readonly store: Store<ApplicationState>,
  ) {}

  public read(listId: string) {
    return this.httpBodyProviderService
      .read<string>(`/api/v2/engagement/productlist/${listId}`)
      .pipe(
        concatLatestFrom(() =>
          this.store.pipe(select(selectActiveAccountBrandOrganizations)),
        ),
        switchMap(([listDataStr, brandOrganizations]) => {
          const listData = JSON.parse(listDataStr) as ProductListResponseDto;

          const brandId = brandOrganizations?.[0]?.brandId ?? '';
          const searchQuery: SearchQuery = {
            useV2Search: true,
            collapse: false,
            withRelatedVariants: true,
            productReferences: listData.productsReference?.map((pr) => ({
              productId: pr.productId,
              brandId,
            })),
          };

          return this.productBasketProviderService.read(searchQuery).pipe(
            map((searchResults) => ({
              listData,
              products: searchResults.products,
            })),
          );
        }),
      );
  }
}
